import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  getAssignedPromoters, getSalesByMonthForPromoters, getTargetsForPromoters,
  getAttendanceByMonth, getAllBonusPrograms, getEventBonuses,
  getPayrollAdjustments, getHolidays, getOvertimeByMonth,
} from "../../firebase/services";
import { computeMonthlyPay, promoterForMonth } from "../../firebase/schema";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";
import { format } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import SalaryBreakdown from "../../components/SalaryBreakdown";

export default function SupervisorSalary() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const { cfgFor, loaded: cfgLoaded } = usePayrollConfig();
  const cfg = useMemo(() => cfgFor(month), [cfgFor, month]);
  const taxCfg = cfg.tax;

  const calculate = async () => {
    if (!user || !cfgLoaded) return;
    setLoading(true);
    const promoters = await getAssignedPromoters(user.uid);
    const ids = promoters.map(p => p.uid);
    const [sales, targets, programs, eventBonuses, adjs, holidays, overtime] = await Promise.all([
      getSalesByMonthForPromoters(month, ids),
      getTargetsForPromoters(month, ids),
      getAllBonusPrograms(),
      getEventBonuses(month, ids),
      getPayrollAdjustments(month),
      getHolidays().catch(() => []),
      getOvertimeByMonth(month).catch(() => []),
    ]);
    const adjMap = {};
    adjs.forEach(a => { adjMap[a.promoterId] = a; });
    const targetMap = {};
    targets.forEach(t => { targetMap[t.promoterId] = t; });

    const results = await Promise.all(promoters.map(async (p0) => {
      const p = promoterForMonth(p0, month);   // lương cơ bản / phụ cấp của đúng tháng này
      const attendance = await getAttendanceByMonth(p.uid, month);
      const pay = computeMonthlyPay({
        promoter: p, month, cfg, attendance,
        sales: sales.filter(x => x.promoterId === p.uid),
        target: targetMap[p.uid] || null,
        programs,
        events: eventBonuses.filter(b => b.promoterId === p.uid),
        adjustment: adjMap[p.uid] || null,
        holidays,
        overtime: overtime.filter(o => o.promoterId === p.uid),
      });
      return { ...p, ...pay, target: targetMap[p.uid] || {} };
    }));
    setRows(results);
    setLoading(false);
  };

  useEffect(() => { calculate(); }, [user, month, cfgLoaded, cfg]);

  return (
    <>
      <div className="page-header">
        <div><h1>{t("salary.title")}</h1><p>{t("salary.subtitle")}</p></div>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
          <button className="btn" onClick={calculate}>{t("common.recalc")}</button>
        </div>
      </div>

      {loading && <div style={{ color: "#888", padding: 20 }}>{t("common.loading")}</div>}

      <div className="card">
        <table className="table-card">
          <thead>
            <tr><th>{t("common.name")}</th><th>{t("salary.days_worked")}</th><th>{t("salary.work_pay")}</th><th>{t("salary.sales_bonus")}</th><th>{t("salary.highend_bonus")}</th><th>{t("salary.program_bonus")}</th><th>{t("salary.event_bonus")}</th><th style={{ textAlign: "right" }}>{t("salary.total")}</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.uid} onClick={() => setSelected(r)} style={{ cursor: "pointer" }}>
                <td data-label="Họ tên"><strong>{r.name}</strong><div style={{ fontSize: 11, color: "#888" }}>{r.shopName}</div></td>
                <td data-label="Ngày công">{r.dayCreditTotal} công{(r.paidHolidayDays || r.holidayCredits) > 0 ? ` (+${r.paidHolidayDays || r.holidayCredits} lễ)` : ""}</td>
                <td data-label="Lương công">{r.workPay?.toLocaleString("vi-VN")} đ</td>
                <td data-label="Thưởng DS">{r.salesBonus > 0 ? r.salesBonus.toLocaleString("vi-VN") + " đ" : <span className="badge badge-gray">0</span>}</td>
                <td data-label="Thưởng HE">{r.highendBonus > 0 ? r.highendBonus.toLocaleString("vi-VN") + " đ" : "—"}</td>
                <td data-label="Thưởng CT">{r.bonusProgram > 0 ? r.bonusProgram.toLocaleString("vi-VN") + " đ" : "—"}</td>
                <td data-label="Thưởng SK">{r.eventBonus > 0 ? r.eventBonus.toLocaleString("vi-VN") + " đ" : "—"}</td>
                <td data-label="Tổng lương"><strong>{r.total?.toLocaleString("vi-VN")} đ</strong></td>
              </tr>
            ))}
            {rows.length === 0 && !loading && <tr><td colSpan={8} style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("salary.no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{t("salary.detail_title")} — {selected.name}</div>
              <button className="btn btn-sm" onClick={() => setSelected(null)}>✕</button>
            </div>
            <SalaryBreakdown month={month} taxCfg={taxCfg} workCfg={cfg.work} row={{
              ...selected,
              sellTarget: selected.target?.sellTarget || 0,
              highendTarget: selected.target?.highendTarget || 0,
            }} />
          </div>
        </div>
      )}
    </>
  );
}
